import {AsyncPipe} from '@angular/common';
import {Component, DestroyRef, OnInit, inject} from '@angular/core';
import {MatDialog} from '@angular/material/dialog';
import {Store} from '@ngrx/store';
import {TranslateService} from '@ngx-translate/core';
import {takeUntilDestroyed} from '@angular/core/rxjs-interop';
import {CardComponent} from '../../../../shared/components/card/card.component';
import {filter, take} from 'rxjs';
import {ConfirmDialogComponent, ConfirmDialogData} from '../../../../shared/components/confirm-dialog/confirm-dialog.component';
import {
  DialogUpdateDeviceGroupComponent,
  DeviceGroupDialogResult
} from '../../../devices/components/dialog-update-device-group/dialog-update-device-group.component';
import { DeviceActions } from '../../../devices/state/device.actions';
import { TabAreaListComponent } from '../../../devices/components/tab-area-list/tab-area-list.component';
import { selectDeviceGroupExpandedById, selectDeviceGroupFilter, selectDeviceGroupsTotalCount, selectFilteredDeviceGroupsWithCurrentPage, selectIsDeviceGroupsLoading } from '../../state/device-groups/device-group.selectors';
import { DeviceGroupActions } from '../../state/device-groups/device-group.actions';
import { DeviceGroupFilter } from '../../state/device-groups/device-group.state';

@Component({
  selector: 'app-page-device-group-list',
  imports: [
    CardComponent,
    AsyncPipe,
    TabAreaListComponent,
  ],
  templateUrl: './page-device-group-list.component.html',
  styleUrl: './page-device-group-list.component.scss',
})
export class PageDeviceGroupListComponent implements OnInit {
  private readonly store = inject(Store);
  private readonly destroyRef = inject(DestroyRef);
  private readonly dialog = inject(MatDialog);
  private readonly translateService = inject(TranslateService);

  readonly loading$ = this.store.select(selectIsDeviceGroupsLoading);
  readonly totalCount$ = this.store.select(selectDeviceGroupsTotalCount);
  readonly deviceGroups$ = this.store.select(selectFilteredDeviceGroupsWithCurrentPage);
  readonly expandedById$ = this.store.select(selectDeviceGroupExpandedById);
  readonly filter$ = this.store.select(selectDeviceGroupFilter);
  currentPage = 0;
  currentPageSize = 12;

  ngOnInit() {
    this.store.dispatch(
      DeviceActions.public.loadAllDevicesByFilters({ filter: {} }),
    );

    this.filter$.pipe(
      takeUntilDestroyed(this.destroyRef),
    ).subscribe((filter) => {
      this.currentPage = filter.page ?? 0;
      this.currentPageSize = filter.pageSize ?? 12;
    });
  }

  onFilterChange(filter: {searchTerm?: string; page?: number; pageSize?: number}) {
    this.store.dispatch(DeviceGroupActions.public.updateDeviceGroupFilter({
      filter: {
        searchTerm: filter.searchTerm ?? undefined,
        page: filter.page ?? 1,
        pageSize: filter.pageSize ?? 12,
      } satisfies DeviceGroupFilter,
    }));
  }

  onTriggerAction(evtData: {id: string, value: boolean}) {
    this.store.dispatch(DeviceGroupActions.public.triggerDeviceGroupAction({
      id: evtData.id,
      value: evtData.value,
    }));
  }

  onCreateDeviceGroup() {
    const dialogRef = this.dialog.open(DialogUpdateDeviceGroupComponent, {
      width: '36rem',
      data: {mode: 'create'},
    });

    dialogRef.afterClosed().pipe(
      take(1),
      filter((result): result is DeviceGroupDialogResult => !!result),
    ).subscribe((result) => {
      this.store.dispatch(DeviceGroupActions.public.createDeviceGroup({requestDto: result.requestDto}));
    });
  }

  onEditDeviceGroup(id: string) {
    this.deviceGroups$.pipe(take(1)).subscribe((deviceGroups) => {
      const deviceGroup = deviceGroups.find((item) => item.id === id);
      if (!deviceGroup) return;

      const dialogRef = this.dialog.open(DialogUpdateDeviceGroupComponent, {
        width: '36rem',
        data: {mode: 'update', deviceGroup},
      });

      dialogRef.afterClosed().pipe(
        take(1),
        filter((result): result is DeviceGroupDialogResult => !!result && !!result.id),
      ).subscribe((result) => {
        this.store.dispatch(DeviceGroupActions.public.updateDeviceGroup({
          id: result.id!,
          requestDto: result.requestDto,
        }));
      });
    });
  }

  onDeleteDeviceGroup(id: string) {
    const dialogData: ConfirmDialogData = {
      title: this.translateService.instant('deviceGroups.delete.title'),
      body: this.translateService.instant('deviceGroups.delete.body'),
      action: 'danger',
      confirmLabel: this.translateService.instant('default.delete'),
    };

    const dialogRef = this.dialog.open(ConfirmDialogComponent, {data: dialogData});
    dialogRef.afterClosed().pipe(
      take(1),
      filter((result): result is true => result === true),
    ).subscribe(() => {
      this.store.dispatch(DeviceGroupActions.public.deleteDeviceGroupById({id}));
    });
  }

  onSetDeviceGroupExpanded(event: {id: string; isExpanded: boolean}) {
    this.store.dispatch(DeviceGroupActions.public.setDeviceGroupExpanded(event));
  }

}
